/**
 * «Подтянуть нормативы урожайности» — счёт пустых строк и итог словами (AK-778).
 *
 * Зачем действие вообще есть. Порядок работы агронома обратный тому, на который
 * был рассчитан код: сначала культуры раскладываются по полям, нормативы
 * урожайности заводятся позже — когда цифры собраны. Норматив подхватывался
 * ТОЛЬКО в момент заведения строки, и подтянуть его к уже разложенному плану
 * было нечем: оставалось снять культуру и завести заново, теряя сорта, цели и
 * назначенные техкарты.
 *
 * Правила счёта живут здесь, а не в компоненте, ровно затем, что на них экран
 * и ловят на вранье: число на кнопке обязано совпасть с тем, что человек видит
 * в таблице, а итог — с тем, что реально произошло на сервере.
 *
 * Что считаем «пустой строкой»: поле, на котором В ЭТОМ варианте стоит культура
 * (`plan_crop_id`), а урожайности нет. Пар в счёт не идёт — это состояние поля,
 * а не культура, урожайности у него не бывает (Р1), и подмешивать его в
 * счётчик значило бы звать человека чинить то, что не сломано.
 */
import type { VariantField } from '@/lib/planning/planCrops';

const HA = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 0 });
const YIELD = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 1 });

/** Культура варианта, у которой есть строки без урожайности. */
export interface YieldGapCrop {
  cropId: string;
  cropName: string;
  /** Строк (полей) этой культуры без урожайности. */
  emptyRows: number;
  /** Га этих строк — «сколько площади не считается в валовке». */
  emptyAreaHa: number;
  /** Норматив хозяйства по культуре. `null` — норматива нет, подтягивать нечего. */
  defaultCHa: number | null;
}

/**
 * Культуры варианта с пустой урожайностью — по убыванию числа строк.
 *
 * Норматив берём с самой строки (`yield_default_c_ha`): его резолвит сервер по
 * лестнице «сезон → хозяйство → компания», и повторять эту лестницу на клиенте
 * значило бы завести второй источник правды, который однажды разойдётся с
 * первым.
 */
export function yieldGapsByCrop(fields: readonly VariantField[]): YieldGapCrop[] {
  const acc = new Map<string, YieldGapCrop>();
  for (const f of fields) {
    if (!f.plan_crop_id) continue;
    if (f.planned_yield_c_ha !== null) continue;
    const row = acc.get(f.plan_crop_id) ?? {
      cropId: f.plan_crop_id,
      cropName: f.plan_crop_name ?? f.occupied_by ?? 'культура',
      emptyRows: 0,
      emptyAreaHa: 0,
      defaultCHa: f.yield_default_c_ha,
    };
    row.emptyRows += 1;
    row.emptyAreaHa += f.area_ha;
    // Норматив на культуру один: если хоть одна строка его знает — знаем и мы.
    if (row.defaultCHa === null) row.defaultCHa = f.yield_default_c_ha;
    acc.set(f.plan_crop_id, row);
  }
  return [...acc.values()].sort(
    (a, b) => b.emptyRows - a.emptyRows || a.cropName.localeCompare(b.cropName, 'ru'),
  );
}

/** Сколько строк варианта сейчас без урожайности — число на кнопке. */
export function emptyYieldRows(gaps: readonly YieldGapCrop[]): number {
  return gaps.reduce((sum, g) => sum + g.emptyRows, 0);
}

/** Га пустых строк — «сколько площади не попадает в валовку». */
export function emptyYieldAreaHa(gaps: readonly YieldGapCrop[]): number {
  return gaps.reduce((sum, g) => sum + g.emptyAreaHa, 0);
}

/**
 * Культуры, которым норматив не заведён: их строки останутся пустыми, и
 * сказать об этом надо ДО нажатия, а не только в итоге.
 */
export function cropsWithoutDefault(gaps: readonly YieldGapCrop[]): YieldGapCrop[] {
  return gaps.filter((g) => g.defaultCHa === null);
}

/** Перечисление культур словами: «нут, лён и ещё 2». */
export function listCropNames(gaps: readonly YieldGapCrop[], limit = 3): string {
  const names = gaps.slice(0, limit).map((g) => g.cropName.toLowerCase());
  const tail = gaps.length > limit ? ` и ещё ${gaps.length - limit}` : '';
  return `${names.join(', ')}${tail}`;
}

/** Подпись выбора «весь вариант»: сколько строк, по скольким культурам, на сколько га. */
export function scopeAllHint(gaps: readonly YieldGapCrop[]): string {
  const rows = emptyYieldRows(gaps);
  const crops = gaps.length;
  return `${rows} ${plural(rows, ROWS)} без урожайности по ${crops} ${plural(crops, CROPS)} · ${HA.format(Math.round(emptyYieldAreaHa(gaps)))} га`;
}

/** Подпись выбора «только эта культура» — с нормативом хозяйства или без него. */
export function scopeCropHint(gap: YieldGapCrop): string {
  const head = `${gap.emptyRows} ${plural(gap.emptyRows, ROWS)} без урожайности · ${HA.format(Math.round(gap.emptyAreaHa))} га`;
  return gap.defaultCHa === null
    ? `${head} · норматива хозяйства нет`
    : `${head} · норматив хозяйства ${YIELD.format(gap.defaultCHa)} ц/га`;
}

export interface ApplyYieldResult {
  /** Строк, куда норматив подставился. */
  updated: number;
  /** Строк со своей цифрой — их не трогали. */
  kept_own: number;
  /** Пустых строк, которым норматив не нашёлся. */
  without_default: number;
}

/**
 * Итог словами — ОДИН на всё действие, с тремя числами.
 *
 * «Оставлено ваших» говорим ВСЕГДА, когда такие строки были: без этой строки
 * действие выглядит разрушительным («а мою цифру не затёрло?»), и человек
 * перестаёт им пользоваться. Третья группа — единственная, где от него ещё
 * что-то требуется, поэтому она называет культуры поимённо: «без норматива 2»
 * без имён — это задача без адреса.
 */
export function applyYieldSummary(
  res: ApplyYieldResult,
  gapsWithoutDefault: readonly YieldGapCrop[],
): string {
  const head =
    res.updated > 0
      ? `Урожайность обновлена: заполнено ${res.updated}`
      : 'Урожайность не изменилась: заполнять было нечего';
  const parts: string[] = [head];
  if (res.kept_own > 0) parts.push(`оставлено ваших ${res.kept_own}`);
  if (res.without_default > 0) parts.push(`без норматива ${res.without_default}`);
  const tail =
    res.without_default > 0 && gapsWithoutDefault.length > 0
      ? ` Норматив урожайности не заведён: ${listCropNames(gapsWithoutDefault)} — по этим культурам урожайность вписывается прямо в строке поля.`
      : '';
  return `${parts.join(' · ')}.${tail}`;
}

const ROWS = ['строка', 'строки', 'строк'] as const;
const CROPS = ['культуре', 'культурам', 'культурам'] as const;

function plural(n: number, forms: readonly [string, string, string]): string {
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 14) return forms[2];
  const mod10 = n % 10;
  if (mod10 === 1) return forms[0];
  if (mod10 >= 2 && mod10 <= 4) return forms[1];
  return forms[2];
}
